// Upload status types
// 'validated' = the document is already human-verified (DocumentStatus.VALIDATED) and was
// deliberately NOT reprocessed by the API (HTTP 409 VALIDATED_IMMUTABLE).
export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error' | 'duplicate' | 'reprocessing' | 'validated'

// Pending file interface for tracking upload state
export interface PendingFile {
  id: string
  file: File
  status: UploadStatus
  progress: number
  error?: string
  result?: UploadResult
}

// Upload result from API
export interface UploadResult {
  id: string
  s3_key: string
  checksum: string
  created_at: string
  document_status: string
  is_duplicate: boolean
  is_reprocessing?: boolean
  message?: string
}

// Upload response from API
export interface UploadResponse {
  id: string
  s3_key: string
  checksum: string
  created_at: string
  document_status: string
  is_duplicate: boolean
  is_reprocessing?: boolean
  message?: string
}

// Accepted file types configuration
export const ACCEPTED_FILE_TYPES = {
  extensions: ['.pdf', '.png', '.jpg', '.jpeg', '.tiff', '.xlsx'],
  mimeTypes: [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/tiff',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
} as const

// Status color mapping for Tailwind CSS classes
export const UPLOAD_STATUS_COLORS: Record<UploadStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  uploading: 'bg-blue-100 text-blue-800',
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  duplicate: 'bg-yellow-100 text-yellow-800',
  reprocessing: 'bg-purple-100 text-purple-800',
  validated: 'bg-emerald-100 text-emerald-800',
}

// Helper function to check if file type is valid
export function isValidFileType(file: File): boolean {
  const extension = `.${file.name.split('.').pop()?.toLowerCase()}`
  return ACCEPTED_FILE_TYPES.extensions.includes(extension as typeof ACCEPTED_FILE_TYPES.extensions[number])
}

// Helper function to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
